/**
 * The events Flow reports, and nothing else. Names are fixed here so a typo cannot create a new, orphan event in the
 * counters (Flow admin › Analytics; `page_view` is sent by `trackPage`). Parameters carry choices, counts and buckets — never what the user typed, a file name, an id, an e-mail or a token.
 *
 * They answer business questions, not "which button was clicked": does a new user get to a working app, how long it takes,
 * where they drop off, whether builds break and recover, whether they come back and ship. The same names and parameter values
 * are sent by the sandbox gateway for Claude Code and Claude apps (`surface: 'claude-code' | 'claude-app'`); the web app always
 * sends `surface: 'web'` (a default parameter, see `setDefaultParams`). Keep this list and the gateway's in step.
 *
 * Which events make someone *active* (they did something, not only opened Flow) is the gateway's list (vfs-service
 * src/gateway/analytics/events.js, shown in Flow admin › Analytics › "What counts as active"): here, every event but
 * page_view, session_outcome, project_return and the results (first_preview, friction).
 *
 * The main customer journey, as a funnel (see docs/ANALYTICS.md):
 *   app_requested → first_preview → change_applied → project_return → publish
 *
 * Buckets (exact strings, see analytics.js): time '<1m' '1-3m' '3-10m' '10-30m' '>30m'; turns '0' '1' '2' '3-5' '6-10' '>10';
 * days '0' '1' '2-7' '8-30' '>30'; duration '<10s' '10-30s' '30-120s' '2-10m' '>10m'.
 */
export const EVENTS = {
  // Activation.
  APP_REQUESTED: 'app_requested', // { surface, mode: 'ask' | 'create' | 'build' | 'simple' | 'full', playbook: <slug> | 'none', has_files, via_voice }
  FIRST_PREVIEW: 'first_preview', // { surface, stack, time_bucket, turns_bucket, build_retries }  (once per project, per browser)
  // Working on it.
  CHANGE_APPLIED: 'change_applied', // { surface, turn_number_bucket, outcome: 'preview_ok' | 'preview_broken' | 'no_preview' }
  UNDO: 'undo', // { surface, how_far: 'last' | 'older' | 'unknown' }  ('unknown': a rollback the agent did)
  CHAT_TURN: 'chat_turn', // { surface, changed }  every finished chat turn with the AI (what was asked is never sent)
  // Coming back and shipping.
  PROJECT_RETURN: 'project_return', // { surface, days_since_bucket }
  PUBLISH: 'publish', // { surface, target: 'zip' | 'gitlab', days_since_create_bucket? }
  // How the session went, and how it felt.
  SESSION_OUTCOME: 'session_outcome', // { surface, result: 'published' | 'working_preview' | 'broken_preview' | 'no_preview', turns_bucket, duration_bucket }
  FEEDBACK: 'feedback', // { surface, rating }
  FRICTION: 'friction', // { surface, area: 'toast' | 'publish' | 'share' | 'prompt_abandoned', recoverable }
};

const KNOWN = new Set(Object.values(EVENTS));
export const isKnownEvent = (name) => KNOWN.has(name);
